'use es6';

import { mapObject, invert } from 'underscore';
import { createSelector } from 'reselect';
import { na1 } from 'hubspot-url-utils/hublets';
import enviro from 'enviro';
import { getField, getFieldBreadCrumbs } from 'DesignData/helpers/customWidgetFieldsHelper';
import { getFields, getFieldsErrors, getFieldsWarnings, makeGetCustomWidgetBufferFields, makeGetIsEmailOrAnyType, makeGetIsGlobal } from 'DesignData/selectors/customWidgetBuffers';
import { isUngated, isScoped as getIsScoped } from 'DesignData/selectors/auth';
import { getIsSalesforceIntegrated } from 'DesignData/selectors/salesforceIntegrationStatus';
import { CMV2_CAN_CREATE_HTML_FIELD, CMV2_CAN_CREATE_HUBL_FIELD, CMV2_CAN_CREATE_SURVEY_FIELD, FOLLOWUP_EMAIL_IN_FORM_FIELD, PAYMENTS_BETA } from 'DesignData/enums/Gates';
import { CTA_ACCESS, WEB_INTERACTIVES_ACCESS, EMAIL_EDITOR_ACCESS, HUBDB_DATA_READ, HUBDB_SCHEMAS_READ, MARKETING_VIDEO, PAYMENT_LINKS_READ, SALES_MEETINGS_ACCESS, SERVICE_FEEDBACK_ACCESS, MESSAGING_ACCESS } from 'DesignData/constants/Scopes';
import * as CustomWidgetFieldTypes from 'ContentUtils/constants/CustomWidgetFieldTypes';
import { EDIT, CREATE } from 'DesignData/enums/CustomWidgetFieldTypePermissions';
import CustomWidgetEmailFieldTypes from 'DesignData/enums/CustomWidgetEmailFieldTypes';
import SeamlessImmutable from 'seamless-immutable';
import { checkHasAccessToCtas } from 'ContentComponents/utils/ctaUtils';
import UrlTypes from 'ContentUtils/enums/UrlTypes';
const EMPTY_IMMUTABLE_OBJECT = SeamlessImmutable({});
const EMPTY_IMMUTABLE_ARRAY = SeamlessImmutable([]);

const getCustomWidgetId = (state, props) => props.assetId || props.customWidgetId;

const getPanesById = state => state.customWidgetEditorOptions.panesById;

const getSelectedFieldById = state => state.customWidgetEditorOptions.selectedFieldById;

const getFieldGroupingById = state => state.customWidgetEditorOptions.fieldGroupingById;

const getIsSelectingIconById = state => state.customWidgetEditorOptions.isSelectingIconById;

const getIsEditingGlobalContentById = state => state.customWidgetEditorOptions.isEditingGlobalContentById;

const getSelectedSmartRuleIdById = state => state.customWidgetEditorOptions.selectedSmartRuleIdById;

const getIsFullScreenEditorOpenById = state => state.customWidgetEditorOptions.isFullScreenEditorOpenById;

const getAccordionStatesById = state => state.customWidgetEditorOptions.accordionStatesById;

const getSelectedLanguageEditorOpenById = state => state.customWidgetEditorOptions.selectedLanguageEditorOpenById;

const getCustomWidgetBufferFields = makeGetCustomWidgetBufferFields();
const getIsEmailOrAnyType = makeGetIsEmailOrAnyType();
const getIsGlobal = makeGetIsGlobal();
export const getAccordionStates = (state, props) => {
  const {
    customWidgetId,
    field
  } = props;
  if (field && field.id) return state.customWidgetEditorOptions.accordionStatesById.getIn([customWidgetId, field.id]);
  return state.customWidgetEditorOptions.accordionStatesById[customWidgetId] || EMPTY_IMMUTABLE_OBJECT;
};
export const getPaneOptions = createSelector([getPanesById, getCustomWidgetId], (panesById, customWidgetId) => {
  return panesById[customWidgetId] || EMPTY_IMMUTABLE_OBJECT;
});
export const getSelectedField = createSelector([getSelectedFieldById, getFields, getCustomWidgetId], (selectedFieldById, fields, customWidgetId) => {
  const selectedField = selectedFieldById[customWidgetId];

  if (selectedField && selectedField.id && fields) {
    return Object.assign({}, selectedField, {
      field: getField(fields.fields, selectedField.id)
    });
  }

  return EMPTY_IMMUTABLE_OBJECT;
});
export const getFieldGrouping = createSelector([getFieldGroupingById, getCustomWidgetId], (fieldGroupingById, customWidgetId) => {
  const fieldGrouping = fieldGroupingById[customWidgetId];
  return fieldGrouping || EMPTY_IMMUTABLE_OBJECT;
});
export const getFieldErrors = createSelector([getFieldsErrors, getSelectedField], (fieldsErrors, selectedField) => {
  const {
    id
  } = selectedField.field || EMPTY_IMMUTABLE_OBJECT;
  return fieldsErrors[id] || EMPTY_IMMUTABLE_OBJECT;
});
export const getFieldWarnings = createSelector([getFieldsWarnings, getSelectedField], (fieldsWarnings, selectedField) => {
  const {
    id
  } = selectedField.field || EMPTY_IMMUTABLE_OBJECT;
  return fieldsWarnings[id] || EMPTY_IMMUTABLE_OBJECT;
});
export const getSelectedFieldBreadcrumbs = createSelector([getSelectedField, getCustomWidgetBufferFields], (selectedField, fieldsList) => {
  const {
    field
  } = selectedField;
  return field ? getFieldBreadCrumbs(fieldsList, field.id) : EMPTY_IMMUTABLE_ARRAY;
});
export const getIsSelectingIcon = createSelector([getIsSelectingIconById, getCustomWidgetId], (isSelectingIconById, customWidgetId) => {
  return isSelectingIconById[customWidgetId] || false;
});
export const getOptionsToSave = createSelector([getPanesById, getSelectedFieldById, getAccordionStatesById], (panesById, selectedFieldById, accordionStatesById) => {
  return {
    panesById,
    selectedFieldById,
    accordionStatesById
  };
});
export const getIsEditingGlobalContent = createSelector([getIsEditingGlobalContentById, getCustomWidgetId], (isEditingGlobalContentById, customWidgetId) => {
  return isEditingGlobalContentById[customWidgetId] || false;
});
export const getSelectedSmartRuleId = createSelector([getSelectedSmartRuleIdById, getCustomWidgetId], (selectedSmartRuleIdById, customWidgetId) => {
  return selectedSmartRuleIdById[customWidgetId] || null;
});
export const getIsFullScreenEditorOpen = createSelector([getIsFullScreenEditorOpenById, getCustomWidgetId], (isFullScreenEditorOpenById, customWidgetId) => {
  return isFullScreenEditorOpenById[customWidgetId] || false;
});
export const getSelectedLanguageEditor = createSelector([getSelectedLanguageEditorOpenById, getCustomWidgetId], (selectedLanguageEditorById, customWidgetId) => {
  return selectedLanguageEditorById[customWidgetId];
});
export const getIsHtmlFieldEnabled = state => isUngated(state, {
  gate: CMV2_CAN_CREATE_HTML_FIELD
});
export const getIsHublFieldEnabled = state => isUngated(state, {
  gate: CMV2_CAN_CREATE_HUBL_FIELD
});

const getIsUngatedForSurveyField = state => isUngated(state, {
  gate: CMV2_CAN_CREATE_SURVEY_FIELD
});

const getIsFollowupEmailFieldDeprecated = state => isUngated(state, {
  gate: FOLLOWUP_EMAIL_IN_FORM_FIELD
});

export const getIsSurveyFieldEnabled = createSelector([getIsUngatedForSurveyField, getIsScoped], (isUngatedForSurveyField, isScoped) => isUngatedForSurveyField && isScoped(SERVICE_FEEDBACK_ACCESS));
export const getIsPaymentsEnabled = state => isUngated(state, {
  gate: PAYMENTS_BETA
});
const DEFAULT_FIELD_TYPE_PERMISSIONS = SeamlessImmutable(mapObject(invert(CustomWidgetFieldTypes), () => {
  return {
    [EDIT]: true,
    [CREATE]: true
  };
}));
export const makeGetFieldTypePermissions = () => {
  let permissions = DEFAULT_FIELD_TYPE_PERMISSIONS;
  return createSelector([getIsHtmlFieldEnabled, getIsGlobal, getIsHublFieldEnabled, getIsSalesforceIntegrated, getIsEmailOrAnyType, getIsSurveyFieldEnabled, getIsPaymentsEnabled, getIsFollowupEmailFieldDeprecated, getIsScoped], (isHtmlFieldEnabled, isGlobal, isHublFieldEnabled, isSalesforceIntegrated, isEmailOrAnyType, isSurveyFieldEnabled, isPaymentsEnabled, isFollowupEmailFieldDeprecated, isScoped) => {
    if (!isHtmlFieldEnabled) {
      permissions = permissions.set(CustomWidgetFieldTypes.HTML, {
        [EDIT]: isGlobal,
        [CREATE]: false
      });
    }

    if (!isHublFieldEnabled) {
      permissions = permissions.set(CustomWidgetFieldTypes.HUBL, {
        [EDIT]: isGlobal,
        [CREATE]: false
      });
    }

    if (!isScoped(EMAIL_EDITOR_ACCESS)) {
      permissions = permissions.merge(mapObject(CustomWidgetEmailFieldTypes, () => {
        return {
          [EDIT]: false,
          [CREATE]: false
        };
      }));
    }

    if (permissions[CustomWidgetFieldTypes.SFDC_CAMPAIGN] !== isSalesforceIntegrated) {
      permissions = permissions.set(CustomWidgetFieldTypes.SFDC_CAMPAIGN, {
        [EDIT]: isSalesforceIntegrated,
        [CREATE]: isSalesforceIntegrated
      });
    }

    if (!isScoped(SALES_MEETINGS_ACCESS)) {
      permissions = permissions.set(CustomWidgetFieldTypes.MEETING, {
        [EDIT]: false,
        [CREATE]: true
      });
    }

    if (!(isScoped(HUBDB_SCHEMAS_READ) && isScoped(HUBDB_DATA_READ))) {
      permissions = permissions.set(CustomWidgetFieldTypes.HUBDB_TABLE, {
        [EDIT]: false,
        [CREATE]: false
      });
    }

    if (!isScoped(CTA_ACCESS) && !isScoped(WEB_INTERACTIVES_ACCESS)) {
      permissions = permissions.set(CustomWidgetFieldTypes.CTA, {
        [EDIT]: true,
        [CREATE]: false
      });
    }

    if (!isScoped(MARKETING_VIDEO)) {
      permissions = permissions.set(CustomWidgetFieldTypes.VIDEO, {
        [EDIT]: true,
        [CREATE]: false
      });
    }

    if (isEmailOrAnyType) {
      permissions = permissions.set(CustomWidgetFieldTypes.ICON, {
        [EDIT]: false,
        [CREATE]: false
      });
      permissions = permissions.set(CustomWidgetFieldTypes.FORM, {
        [EDIT]: false,
        [CREATE]: false
      });
    }

    if (!isSurveyFieldEnabled) {
      permissions = permissions.set(CustomWidgetFieldTypes.SURVEY, {
        [EDIT]: false,
        [CREATE]: false
      });
    }

    if (!(isScoped(HUBDB_SCHEMAS_READ) && isScoped(HUBDB_DATA_READ))) {
      permissions = permissions.set(CustomWidgetFieldTypes.HUBDB_ROW, {
        [EDIT]: false,
        [CREATE]: false
      });
    }

    if (!(isScoped(PAYMENT_LINKS_READ) && isPaymentsEnabled && enviro.getHublet() === na1)) {
      permissions = permissions.set(CustomWidgetFieldTypes.PAYMENT, {
        [EDIT]: false,
        [CREATE]: false
      });
    }

    if (isFollowupEmailFieldDeprecated) {
      permissions = permissions.set(CustomWidgetFieldTypes.FOLLOW_UP_EMAIL, {
        [EDIT]: true,
        [CREATE]: false
      });
    } // Deprecating the workflow field;


    permissions = permissions.set(CustomWidgetFieldTypes.WORKFLOW, {
      [EDIT]: true,
      [CREATE]: false
    });
    return permissions;
  });
};
const getFieldTypePermissions = makeGetFieldTypePermissions();
export const getFieldCreateOptions = createSelector([getIsEmailOrAnyType, getFieldTypePermissions, getIsScoped], (isEmailOrAnyType, fieldTypePermissions, isScoped) => {
  return {
    isEmailOrAnyType,
    urlSupportedTypePermissions: {
      [UrlTypes.PAYMENT]: fieldTypePermissions[CustomWidgetFieldTypes.PAYMENT][CREATE],
      [UrlTypes.CALL_TO_ACTION]: checkHasAccessToCtas(),
      [UrlTypes.WHATSAPP_NUMBER]: isScoped(MESSAGING_ACCESS)
    }
  };
});